import React, { useRef, useState } from 'react';
import { compressImage } from '../services/compressImage';
import { getOS } from '../services/installPrompt';
import CameraCapture from './CameraCapture';
import styles from './MeterField.module.css';

const IS_IOS = getOS() === 'ios';

const META = {
  luz:  { label: 'Medidor de Luz',  emoji: '⚡', color: '#F59E0B' },
  agua: { label: 'Medidor de Agua', emoji: '💧', color: '#3B82F6' },
  gas:  { label: 'Medidor de Gas',  emoji: '🔥', color: '#EF4444' },
};

const MOTIVOS_SIN_ACCESO = [
  'Caja/cuarto cerrado con llave',
  'Medidor dañado o destruido',
  'Acceso bloqueado por obras',
  'Residente no permite el acceso',
  'Medidor no encontrado',
  'Mala iluminación — no es posible fotografiar',
  'Otro',
];

export default function MeterField({ tipo, data, onChange, onFile, isOnline = true, lecturaRequerida = false }) {
  const inputRef = useRef();
  const meta     = META[tipo];

  const [imgLoading,     setImgLoading]     = useState(() => !!data.preview);
  const [imgError,       setImgError]       = useState(false);
  const [sinAcceso,      setSinAcceso]      = useState(() => data.sin_acceso ? true : false);
  const [motivoAcceso,   setMotivoAcceso]   = useState(() => data.motivo_sin_acceso || '');
  const [showCamera,     setShowCamera]     = useState(false);
  // lecturaSaved: true cuando el auditor confirmó la lectura con el botón Guardar
  const [lecturaSaved,   setLecturaSaved]   = useState(() => !!data.lectura);

  const processFile = async (file) => {
    // Capturar hora de la foto ANTES de comprimir:
    // canvas.toBlob() crea un nuevo JPEG sin EXIF, por lo que la hora debe
    // extraerse del archivo original. file.lastModified es el instante de
    // captura para fotos tomadas directamente con la cámara del dispositivo.
    const horaFoto = new Date(file.lastModified).toISOString();
    onChange('hora_foto', horaFoto);

    onChange('preview', URL.createObjectURL(file));
    onChange('lectura', '');
    onChange('foto_path', null);
    onChange('lectura_guardada', false);
    setImgLoading(true);
    setImgError(false);
    setLecturaSaved(false);
    setSinAcceso(false);
    setMotivoAcceso('');
    onChange('sin_acceso', false);
    onChange('motivo_sin_acceso', null);

    let fileToUpload = file;
    try { fileToUpload = await compressImage(file); } catch { /* usar original */ }

    // Siempre guardar local — se sube al servidor solo al presionar "Guardar visita"
    onChange('foto', fileToUpload);
    onFile?.(fileToUpload);
  };

  // Handler para <input file> (fallback)
  const handleFile = async e => {
    const file = e.target.files[0];
    if (!file) return;
    await processFile(file);
  };

  // Handler para CameraCapture
  const handleCameraCapture = async (file) => {
    setShowCamera(false);
    await processFile(file);
  };

  const remove = () => {
    onChange('foto', null);
    onChange('preview', null);
    onChange('lectura', '');
    onChange('foto_path', null);
    onChange('lectura_guardada', false);
    setLecturaSaved(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const confirmarSinAcceso = () => {
    if (!motivoAcceso) return;
    setSinAcceso(true);
    onChange('sin_acceso', true);
    onChange('motivo_sin_acceso', motivoAcceso);
  };

  const cancelarSinAcceso = () => {
    setSinAcceso(false);
    setMotivoAcceso('');
    onChange('sin_acceso', false);
    onChange('motivo_sin_acceso', null);
  };

  // Medidor confirmado sin acceso
  if (sinAcceso === true) {
    return (
      <div className={styles.card}>
        <div className={styles.header} style={{ borderColor: meta.color }}>
          <span className={styles.emoji}>{meta.emoji}</span>
          <span className={styles.label}>{meta.label}</span>
        </div>
        <div className={styles.sinAccesoBox}>
          <span className={styles.sinAccesoIcon}>📵</span>
          <div>
            <span className={styles.sinAccesoLabel}>No se puede capturar evidencia</span>
            <span className={styles.sinAccesoMotivo}>{motivoAcceso}</span>
          </div>
          <button className={styles.btnCancelarAcceso} onClick={cancelarSinAcceso}>✕</button>
        </div>
      </div>
    );
  }

  return (
    <>
    {/* Visor de cámara fullscreen con control de flash */}
    {showCamera && (
      <CameraCapture
        onCapture={handleCameraCapture}
        onCancel={() => setShowCamera(false)}
      />
    )}
    <div className={styles.card}>
      <div className={styles.header} style={{ borderColor: meta.color }}>
        <span className={styles.emoji}>{meta.emoji}</span>
        <span className={styles.label}>{meta.label}</span>
      </div>

      {/* Foto o botones de captura */}
      {data.preview ? (
        <div className={styles.previewWrap}>
          {imgLoading && !imgError && <div className={styles.imgSkeleton} />}
          {imgError ? (
            <div className={styles.imgError}>
              <span>📷</span>
              <span>No se pudo cargar la imagen</span>
            </div>
          ) : (
            <img
              src={data.preview}
              alt={`Foto ${tipo}`}
              className={styles.preview}
              style={{ display: imgLoading ? 'none' : 'block' }}
              onLoad={() => setImgLoading(false)}
              onError={() => {
                if (data.foto_file) {
                  onChange('preview', URL.createObjectURL(data.foto_file));
                  setImgLoading(true);
                  setImgError(false);
                } else {
                  setImgLoading(false);
                  setImgError(true);
                }
              }}
            />
          )}
          <button className={styles.removeBtn} onClick={() => {
            remove();
            IS_IOS ? inputRef.current?.click() : setShowCamera(true);
          }}>✕ Cambiar foto</button>
        </div>
      ) : (
        <div className={styles.captureRow}>
          <button className={styles.photoBtn} onClick={() => {
            IS_IOS ? inputRef.current?.click() : setShowCamera(true);
          }}>
            <span>📷</span>
            <span>Tomar foto</span>
          </button>
          <button className={styles.sinAccesoBtn} onClick={() => setSinAcceso('seleccionar')}>
            📵 Sin evidencia
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        style={{ display: 'none' }}
      />

      {/* Seleccionar motivo sin acceso */}
      {sinAcceso === 'seleccionar' && (
        <div className={styles.sinAccesoForm}>
          <label className={styles.sinAccesoFormLabel}>¿Por qué no se puede capturar evidencia?</label>
          <select
            value={motivoAcceso}
            onChange={e => setMotivoAcceso(e.target.value)}
            className={styles.sinAccesoSelect}
          >
            <option value="">Seleccionar motivo...</option>
            {MOTIVOS_SIN_ACCESO.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <div className={styles.sinAccesoActions}>
            <button
              className={styles.btnConfirmarAcceso}
              onClick={confirmarSinAcceso}
              disabled={!motivoAcceso}
            >
              Confirmar
            </button>
            <button
              className={styles.btnCancelarAcceso}
              onClick={() => { setSinAcceso(false); setMotivoAcceso(''); }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lectura confirmada → vista verde con botón Editar */}
      {data.preview && lecturaSaved && data.lectura && (
        <div className={styles.resultOk}>
          <div className={styles.resultRow}>
            <div className={styles.resultValor}>
              <span className={styles.resultLabel}>Lectura registrada</span>
              <span className={styles.resultNum}>{data.lectura}</span>
            </div>
            <button
              className={styles.btnCorregir}
              onClick={() => { setLecturaSaved(false); onChange('lectura_guardada', false); }}
            >
              ✏️ Editar
            </button>
          </div>
        </div>
      )}

      {/* Lectura: input + botón Guardar */}
      {data.preview && !lecturaSaved && (
        <div className={`${styles.editBox} ${lecturaRequerida ? styles.editBoxRequerida : ''}`}>
          <label className={styles.editLabel}>
            Ingresa la lectura del medidor:{lecturaRequerida && <span className={styles.requeridoBadge}> obligatoria</span>}
          </label>
          <div className={styles.inputRow}>
            <input
              type="text"
              inputMode="decimal"
              value={data.lectura}
              onChange={e => onChange('lectura', e.target.value.replace(/\./g, ','))}
              placeholder="Ej: 00201,234"
              autoFocus
            />
            <button
              className={styles.btnGuardar}
              onClick={() => { setLecturaSaved(true); onChange('lectura_guardada', true); }}
              disabled={!data.lectura}
            >
              ✓
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
